'use client';

/**
 * Slim guest-conversion banner. Renders only for GUEST accounts and only
 * until dismissed (session-scoped, so it returns next session rather than
 * nagging forever). One line + a Sign-up action that upgrades the guest in
 * place via the /signup dialog.
 *
 * The copy claims the TRUE thing everywhere the banner shows: a guest's work
 * already persists on the guest account, so "sign up to save your work" was a
 * false alarm inside apps that had just saved it. What is actually at stake
 * is the guest session itself — browser-bound and reaped after inactivity
 * (lib/auth/guest-gc.ts) — so the line sells keeping the work, not saving it.
 *
 * Two placements sharing one dismissal:
 * - `panel` (default): the foot of the left panel body, below the active
 *   view, so it appears across every panel view without each view knowing
 *   about it. The account view opts out — it has its own guest card.
 * - `floating`: a pill centered at the top of the workspace canvas (railV2
 *   keeps the canvas top clean, so the panel variant is invisible while the
 *   panel is closed — this one is the always-visible reminder).
 */

import { useEffect, useState } from 'react';
import { useT } from '@/components/i18n-provider';
import { LogIn, X } from 'lucide-react';
import { useAuth } from '@/components/auth-provider';
import { useFullscreenNav } from '@/components/workspace/fullscreen-nav';
import { isGuestUser } from '@/lib/auth/is-guest';

const DISMISS_KEY = 'guestSaveBannerDismissed';

export function GuestSaveBanner({ variant = 'panel' }: { variant?: 'panel' | 'floating' }) {
  const t = useT();
  const { user } = useAuth();
  const { openHref } = useFullscreenNav();
  const [dismissed, setDismissed] = useState(true); // assume dismissed until we read storage (no SSR flash)

  useEffect(() => {
    try {
      setDismissed(sessionStorage.getItem(DISMISS_KEY) === '1');
    } catch {
      setDismissed(false);
    }
  }, []);

  if (dismissed || !isGuestUser(user)) return null;

  const dismiss = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem(DISMISS_KEY, '1');
    } catch {
      /* storage blocked — hide for this render only */
    }
  };

  if (variant === 'floating') {
    return (
      <div className="pointer-events-none absolute left-1/2 top-3 z-30 w-full -translate-x-1/2 px-2">
        {/* The TEXT is the one flexible child (`min-w-0 truncate`): on a
            narrow or font-boosted viewport the pill must shrink its copy,
            never push the dismiss X outside its own capsule — which is
            exactly what shrink-0 children in a width-capped flex box did. */}
        {/* Button-first (user call): the old order said "Sign up" twice —
            once in the copy, once on the button. The button IS the message;
            the copy shrinks to the reason. */}
        <div className="pointer-events-auto mx-auto flex w-fit max-w-full items-center gap-2 rounded-full border border-primary/25 bg-background/85 p-1.5 shadow-sm backdrop-blur-md">
          <button
            type="button"
            onClick={() => openHref('/signup')}
            className="inline-flex shrink-0 items-center gap-1 rounded-full bg-primary px-2.5 py-1 text-[12px] font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
          >
            <LogIn className="h-3.5 w-3.5" />
            {t('home.cta.signUp')}
          </button>
          <span className="min-w-0 truncate text-[12px] leading-snug text-foreground/80">
            {t('guestBanner.saveWork')}
          </span>
          {/* Sized to the pill's END CAP: 28px inside 6px of padding on a
              40px pill, so the hover disc is concentric with the rounded end
              instead of a 22px circle floating in the slack and colliding
              with the corner curve. Muted hover too — dismissing is the
              quietest thing on this banner, not an accent action. */}
          <button
            type="button"
            onClick={dismiss}
            aria-label={t('actions.dismiss')}
            className="-ml-1.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-foreground/10 hover:text-foreground active:bg-foreground/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="shrink-0 border-t border-primary/20 bg-primary/[0.06] px-3 py-2">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => openHref('/signup')}
          className="inline-flex shrink-0 items-center gap-1 rounded-md bg-primary px-2.5 py-1 text-[12px] font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
        >
          <LogIn className="h-3.5 w-3.5" />
          {t('home.cta.signUp')}
        </button>
        <span className="min-w-0 flex-1 truncate text-[12px] leading-snug text-foreground/80">
          {t('guestBanner.saveWork')}
        </span>
        <button
          type="button"
          onClick={dismiss}
          aria-label={t('actions.dismiss')}
          className="-ml-1 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-foreground/10 hover:text-foreground active:bg-foreground/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
